import { useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { gameMembers } from '../state/index';
import { ShowGameMemberItem } from '../types/crash';
import { isLoggedIn, userInfoState } from '@/state/userInfo';
import {
  CrashBetRecordsBetItem,
  currentCrashBetRecords,
} from '@/apis/casino/crash';
import { useQuery } from '@tanstack/react-query';
import { getMultiplier } from '@/components/features/casino/crash/utils';
import { formatMoneyByCurrency } from '@/utils';

export const updateMemberList = (
  old: CrashBetRecordsBetItem[],
  added: CrashBetRecordsBetItem[],
) => {
  const ids = old.map((item) => item.userId);
  const newList = [...old];
  added.forEach((item) => {
    if (!ids.includes(item.userId)) {
      //add
      newList.push(item);
    } else {
      //update
      const i = newList.findIndex((n) => n.userId === item.userId);
      newList.splice(i, 1, {
        ...newList[i],
        ...item,
      });
    }
  });
  return newList;
};

export function formatMember(
  member: CrashBetRecordsBetItem,
): ShowGameMemberItem {
  const amount = member.amount ? member.amount : 0;
  const cashOutAt = member.cashOutAt ? member.cashOutAt : 0;
  return {
    ...member,
    amount,
    cashOutAt,
    showPlayer: member.userName ? member.userName : 'hidden',
    showMultiplier: member.payoutMultiplier
      ? getMultiplier(member.payoutMultiplier)
      : '-',
    showBet: formatMoneyByCurrency(amount, { currency: member.currency }),
    showProfit: formatMoneyByCurrency(member.payout, {
      currency: member.currency,
    }),
  };
}

export const useMemberList = () => {
  const loggedIn = useAtomValue(isLoggedIn);

  const { data: ranks, isLoading } = useQuery({
    queryKey: ['crash', 'memberList'],
    queryFn: async () => {
      const res = await currentCrashBetRecords();
      if (res.status.code === '0') {
        return res.data.bets.map((item) => formatMember(item));
      } else {
        return [];
      }
    },
    enabled: loggedIn,
  });
  const { userMetaData } = useAtomValue(userInfoState);
  const validateIsCurrentUser = (userId) => {
    if (!userMetaData) return false;
    return userMetaData?.uid === userId;
  };
  // refresh page
  useEffect(() => {
    if (!ranks) return;
    if (ranks?.length === 0) return;
    setMemberList(updateMemberList(memberList, ranks));
  }, [ranks]);

  const [memberList, setMemberList] = useAtom(gameMembers);

  const crashedMembers = useMemo(() => {
    return memberList.filter((item) => item.payout > 0);
  }, [memberList]);

  const clear = () => {
    setMemberList([]);
  };

  const init = (data) => {
    setMemberList(data);
  };
  const update = (added) => {
    if (!added) return;
    setMemberList(updateMemberList(memberList, added));
  };

  const list = memberList.map((item) => {
    const tmp = { ...formatMember(item) };
    const isCurrentUser = validateIsCurrentUser(item.userId);
    return {
      ...tmp,
      isHidden: item.userName === 'hidden',
      isCurrentUser,
      currentUserIsBet: isCurrentUser && item.amount > 0,
      showPlayer: isCurrentUser ? userMetaData?.name : item.userName,
    };
  });
  return {
    isLoading,
    memberList: list,
    crashedMembers: crashedMembers.map((item) => formatMember(item)),
    init,
    clear,
    update,
  };
};
export const useUpdateMemberList = () => {
  const setMemberList = useSetAtom(gameMembers);
  return (added) => {
    if (!added) return;
    setMemberList((memberList) => {
      return updateMemberList(memberList, added);
    });
  };
};

export const useClearMemberList = () => {
  const setMemberList = useSetAtom(gameMembers);
  return () => {
    setMemberList([]);
  };
};
export const useInitMemberList = () => {
  const setMemberList = useSetAtom(gameMembers);
  return (data) => {
    setMemberList(data);
  };
};
